import KybRepository from "@/core/kyb/repositories/KybRepository";
import {
  EntrepriseIdentification,
  Immatriculation,
  ImmatriculationStatus,
  TaxIdentifier,
} from "@/core/kyb/entities/EntrepriseIdentification";
import {
  RegistrationProofUploadResult,
  RegistrationVerificationDecisionInput,
  RegistrationVerificationDecisionResult,
} from "@/core/kyb/entities/RegistrationVerification";
import {
  AddRepresentativeCommand,
  AddRepresentativeOutcome,
  KybPersonCandidate,
  KybPersonCandidateMatchType,
  KybPersonCandidateOrigin,
  KybPersonRole,
  KybRelationRole,
  KybRelationRoleCategory,
  KybRelationScreen,
  KybRepresentativePerson,
  KybRepresentativeSource,
  KybRoleVerificationStatus,
  KybScreeningOutcome,
} from "@/core/kyb/entities/KybRepresentative";
import {
  generateMockRelationRoles,
  generateMockRepresentativeCandidates,
} from "@/core/kyb/entities/representativeCatalogMock";
import { Shareholding } from "@/core/kyb/entities/Shareholding";
import {
  AddLegalPartyCommand,
  AddOwnershipEdgeCommand,
  KybBeneficialOwner,
  KybBeneficialOwnerOrigin,
  KybLegalParty,
  KybOpacityAssessment,
  KybOpacityLevel,
  KybOwnershipEdge,
  KybOwnershipNature,
  KybOwnershipSource,
  KybPartyType,
} from "@/core/kyb/entities/KybOwnership";
import {
  DocumentChecklist,
  DocumentChecklistItem,
  CaseDocumentStatus,
  CaseDocumentUploadResult,
  DocumentObligation,
} from "@/core/kyb/entities/DocumentChecklist";
import {
  ScreeningResult,
  ScreeningSubject,
} from "@/core/kyb/entities/Screening";
import { RiskScore, RiskScoreInputs } from "@/core/kyb/entities/RiskScore";
import { KybDecision } from "@/core/kyb/entities/Decision";
import { AccountOpeningResult } from "@/core/kyb/entities/AccountOpening";
import { EvidencePack } from "@/core/kyb/entities/EvidencePack";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import { isKybEntityMockEnabled } from "@/core/env/mockFlags";
import { isMockActiveSignatory } from "@/core/kyb/entities/activeSignatoryMock";
import { generateMockDocumentChecklist } from "@/core/kyb/entities/documentChecklistMock";
import { generateMockScreeningResults } from "@/core/kyb/entities/screeningMock";
import { generateMockRiskScore } from "@/core/kyb/entities/riskScoreMock";
import { generateMockAccountNumber } from "@/core/kyb/entities/accountOpeningMock";
import {
  EvidencePackGenerationInputs,
  generateMockEvidencePack,
} from "@/core/kyb/entities/evidencePackMock";
import {
  DecisionResolutionInputs,
  generateMock4EyesDecision,
} from "@/core/kyb/entities/decisionMock";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import {
  executeRequest,
  executeVoidRequest,
} from "../../http/services/executeRequest/executeRequest";
import handleRequestError from "../../http/services/handleRequestError/handleRequestError";
import kybEndpoints from "../../api/kybEndpoints/kybEndpoints";
import { updateCustomerKYCUrl } from "../../api/customersBackendEndpoints/customersBackendEndpoints";

/** Bloc `legalEntity` du PATCH `/customers/prospect/{businessKey}` — mise à jour partielle, seuls
 * les champs fournis (non `undefined`) sont envoyés. */
type LegalEntityDetailsPatchDto = {
  shareCapital?: number;
  incorporationDate?: string;
  headOfficeAddress?: string;
  corporatePurpose?: string;
  sectorActivityCode?: string;
  tradeName?: string;
  city?: string;
};

type KybLegalEntityCaseDto = {
  caseId: string;
};

type KybCaseDocumentDto = {
  caseDocumentId: string;
  caseId: string;
  typePieceCode: string;
  obligation: DocumentObligation;
  quantiteAttendue: number;
  baseReglementaire: string;
  statut: CaseDocumentStatus;
  satisfaite: boolean;
  documentId: string | null;
  dateExpiration: string | null;
  commentaire: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
};

type KybCaseDocumentChecklistDto = {
  determine: boolean;
  complet: boolean;
  tauxCompletude: number;
  nombreAttendu: number;
  nombreSatisfait: number;
  pieces: KybCaseDocumentDto[];
  manquantes: KybCaseDocumentDto[];
  expirees: KybCaseDocumentDto[];
};

function mapChecklistItem(dto: KybCaseDocumentDto): DocumentChecklistItem {
  return {
    caseDocumentId: dto.caseDocumentId,
    typePieceCode: dto.typePieceCode,
    obligation: dto.obligation,
    quantiteAttendue: dto.quantiteAttendue,
    baseReglementaire: dto.baseReglementaire,
    statut: dto.statut,
    satisfaite: dto.satisfaite,
    documentId: dto.documentId ?? undefined,
    dateExpiration: dto.dateExpiration ?? undefined,
    commentaire: dto.commentaire ?? undefined,
    updatedBy: dto.updatedBy ?? undefined,
    updatedAt: dto.updatedAt ?? undefined,
  };
}

function mapChecklist(
  businessKey: string,
  dto: KybCaseDocumentChecklistDto,
): DocumentChecklist {
  return {
    businessKey,
    determine: dto.determine,
    complet: dto.complet,
    tauxCompletude: dto.tauxCompletude,
    pieces: dto.pieces.map(mapChecklistItem),
  };
}

/** Miroir de `KybRelatedPersonDocumentResponse` (dépôt niveau dossier, écran 05). */
type KybCaseDocumentUploadDto = {
  personDocumentId: string;
  typePieceCode: string;
  fileName: string;
};

type KybEntityRegistrationDto = {
  registrationId: string;
  idType: string;
  idValue: string;
  countryCode: string;
  issuedDate: string | null;
  verificationStatus: ImmatriculationStatus;
};

type KybEntityRegistrationProofDto = {
  proofDocumentId: string;
  sha256: string;
  fileName: string;
};

type KybEntityRegistrationVerifyResponseDto = {
  verificationStatus: ImmatriculationStatus;
  verificationSource: string | null;
  verifiedBy: string | null;
  verifiedAt: string | null;
};

const registrationKey = (idType: string, idValue: string): string =>
  `${idType}|${idValue}`;

type KybRelationRoleDto = {
  code: string;
  label: string;
  category: KybRelationRoleCategory;
  categoryLabel: string;
  partieLieeType: string | null;
};

function mapRelationRole(dto: KybRelationRoleDto): KybRelationRole {
  return {
    code: dto.code,
    label: dto.label,
    category: dto.category,
    categoryLabel: dto.categoryLabel,
    relatedPartyNature:
      (dto.partieLieeType as KybRelationRole["relatedPartyNature"]) ??
      undefined,
  };
}

type KybRepresentativeDto = {
  relatedPersonId: string;
  roleCode: string;
  roleLabel: string | null;
  roleCategory: "DIRIGEANT" | "SIGNATAIRE";
  lastName: string;
  firstName: string;
  birthDate: string | null;
  nationalityCode: string | null;
  professionCode: string | null;
  personCustomerId: string | null;
  source: KybRepresentativeSource | null;
  screeningOutcome: KybScreeningOutcome | null;
  projectedAt: string | null;
  createdAt: string;
};

function mapRepresentative(dto: KybRepresentativeDto): KybRepresentativePerson {
  return {
    relatedPersonId: dto.relatedPersonId,
    roleCode: dto.roleCode,
    roleLabel: dto.roleLabel ?? undefined,
    roleCategory: dto.roleCategory,
    lastName: dto.lastName,
    firstName: dto.firstName,
    birthDate: dto.birthDate ?? undefined,
    nationalityCode: dto.nationalityCode ?? undefined,
    professionCode: dto.professionCode ?? undefined,
    personCustomerId: dto.personCustomerId ?? undefined,
    source: dto.source ?? "AGENT_INPUT",
    screeningOutcome: dto.screeningOutcome ?? undefined,
    projectedAt: dto.projectedAt ?? undefined,
    createdAt: dto.createdAt,
  };
}

type KybPersonCandidateDto = {
  origin: KybPersonCandidateOrigin;
  matchType: KybPersonCandidateMatchType;
  relatedPersonId: string | null;
  personCustomerId: string | null;
  lastName: string;
  firstName: string;
  birthDate: string | null;
  nationalityCode: string | null;
  screeningOutcome: KybScreeningOutcome | null;
  roleCodes: string[];
};

function mapPersonCandidate(dto: KybPersonCandidateDto): KybPersonCandidate {
  return {
    origin: dto.origin,
    matchType: dto.matchType,
    relatedPersonId: dto.relatedPersonId ?? undefined,
    personCustomerId: dto.personCustomerId ?? undefined,
    lastName: dto.lastName,
    firstName: dto.firstName,
    birthDate: dto.birthDate ?? undefined,
    nationalityCode: dto.nationalityCode ?? undefined,
    screeningOutcome: dto.screeningOutcome ?? undefined,
    roleCodes: dto.roleCodes,
  };
}

type KybPersonRoleDto = {
  personRoleId: string;
  relatedPersonId: string;
  roleCode: string;
  roleLabel: string | null;
  roleCategory: "DIRIGEANT" | "SIGNATAIRE" | null;
  verificationStatus: KybRoleVerificationStatus;
  createdAt: string;
};

function mapPersonRole(dto: KybPersonRoleDto): KybPersonRole {
  return {
    personRoleId: dto.personRoleId,
    relatedPersonId: dto.relatedPersonId,
    roleCode: dto.roleCode,
    roleLabel: dto.roleLabel ?? undefined,
    roleCategory: dto.roleCategory ?? undefined,
    verificationStatus: dto.verificationStatus,
    createdAt: dto.createdAt,
  };
}

type KybLegalPartyDto = {
  relatedPersonId: string;
  roleCode: string;
  roleLabel: string | null;
  denomination: string;
  legalFormCode: string | null;
  registrationId: string | null;
  countryCode: string | null;
  source: KybOwnershipSource | null;
  createdAt: string;
};

function mapLegalParty(dto: KybLegalPartyDto): KybLegalParty {
  return {
    relatedPersonId: dto.relatedPersonId,
    roleCode: dto.roleCode,
    roleLabel: dto.roleLabel ?? undefined,
    denomination: dto.denomination,
    legalFormCode: dto.legalFormCode ?? undefined,
    registrationId: dto.registrationId ?? undefined,
    countryCode: dto.countryCode ?? undefined,
    source: dto.source ?? "AGENT_INPUT",
    createdAt: dto.createdAt,
  };
}

type KybOwnershipEdgeDto = {
  ownershipEdgeId: string;
  ownerPartyId: string;
  ownerLabel: string | null;
  ownerPartyType: KybPartyType | null;
  ownedPartyId: string | null;
  ownedLabel: string | null;
  rootTarget: boolean;
  nature: KybOwnershipNature | null;
  percentage: number | null;
  controlBasis: string | null;
  source: KybOwnershipSource | null;
  createdAt: string;
};

function mapOwnershipEdge(dto: KybOwnershipEdgeDto): KybOwnershipEdge {
  return {
    ownershipEdgeId: dto.ownershipEdgeId,
    ownerPartyId: dto.ownerPartyId,
    ownerLabel: dto.ownerLabel ?? undefined,
    ownerPartyType: dto.ownerPartyType ?? undefined,
    ownedPartyId: dto.ownedPartyId ?? undefined,
    ownedLabel: dto.ownedLabel ?? undefined,
    rootTarget: dto.rootTarget,
    // La nature est garantie non nulle par le back (400 sinon à la création) ; le typage optionnel
    // du DTO ne couvre que la robustesse de lecture, pas un état réellement atteignable.
    nature: dto.nature ?? "CAPITAL",
    percentage: dto.percentage ?? undefined,
    controlBasis: dto.controlBasis ?? undefined,
    source: dto.source ?? "AGENT_INPUT",
    createdAt: dto.createdAt,
  };
}

type KybBeneficialOwnerDto = {
  partyId: string;
  displayName: string | null;
  origin: KybBeneficialOwnerOrigin;
  originLabel: string | null;
  effectiveCapitalPercent: number | null;
  effectiveVotePercent: number | null;
  controlBasis: string | null;
  thresholdPercent: number;
};

function mapBeneficialOwner(dto: KybBeneficialOwnerDto): KybBeneficialOwner {
  return {
    partyId: dto.partyId,
    displayName: dto.displayName ?? undefined,
    origin: dto.origin,
    originLabel: dto.originLabel ?? undefined,
    effectiveCapitalPercent: dto.effectiveCapitalPercent ?? undefined,
    effectiveVotePercent: dto.effectiveVotePercent ?? undefined,
    controlBasis: dto.controlBasis ?? undefined,
    thresholdPercent: dto.thresholdPercent,
  };
}

type KybOpacityContributionDto = {
  indicator: string;
  indicatorLabel: string | null;
  detected: boolean;
  measure: number;
  weight: number;
  contribution: number;
  detail: string | null;
};

type KybOpacityAssessmentDto = {
  caseId: string;
  score: number;
  level: KybOpacityLevel;
  levelLabel: string | null;
  eddRecommended: boolean;
  eddThreshold: number;
  contributions: KybOpacityContributionDto[];
};

function mapOpacityAssessment(dto: KybOpacityAssessmentDto): KybOpacityAssessment {
  return {
    caseId: dto.caseId,
    score: dto.score,
    level: dto.level,
    levelLabel: dto.levelLabel ?? undefined,
    eddRecommended: dto.eddRecommended,
    eddThreshold: dto.eddThreshold,
    contributions: dto.contributions.map((c) => ({
      indicator: c.indicator,
      indicatorLabel: c.indicatorLabel ?? undefined,
      detected: c.detected,
      measure: c.measure,
      weight: c.weight,
      contribution: c.contribution,
      detail: c.detail ?? undefined,
    })),
  };
}

/**
 * AC-2 : le back renvoie un 409 avec les candidats dans `data` quand un doublon est détecté.
 * `DefaultHttpClient` reconnaît ce cas ({@link HttpClient} → type `CONFLICT`) et rejette avec
 * `{ code, message, data, type: "CONFLICT" }` — extrait la liste si c'est bien cette forme,
 * `null` sinon pour laisser le chemin d'erreur générique gérer le reste (dossier clos, etc.,
 * qui sont aussi des 409 mais sans candidats à proposer).
 */
function extractDuplicateCandidates(error: unknown): KybPersonCandidateDto[] | null {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const { type, data } = error as { type?: string; data?: unknown };
  if (type !== "CONFLICT" || !Array.isArray(data)) {
    return null;
  }
  return data as KybPersonCandidateDto[];
}

// Le back persiste immatriculations et identifiants fiscaux dans la même ressource
// (kyb_entity_registration, un seul `idType`) ; le front les répartit en deux sections
// distinctes (ImmatriculationsSection / TaxIdentifiersSection). "AUTRE" existe des deux
// côtés dans l'UI : à l'hydratation, seuls les types fiscaux connus vont dans les
// identifiants fiscaux, tout le reste (RCCM, AUTRE, type inconnu) reste une immatriculation.
const TAX_IDENTIFIER_ID_TYPES = new Set(["IFU", "NIF", "NINEA"]);

export default class KybRepositoryBackend implements KybRepository {
  constructor(private readonly httpClient: HttpClient) {}

  private async fetchExistingRegistrations(
    caseId: string,
  ): Promise<Map<string, KybEntityRegistrationDto>> {
    const response = await this.httpClient.get<
      ApiResponse<KybEntityRegistrationDto[]>
    >({
      url: kybEndpoints.entityRegistrations(caseId),
      withAuth: true,
    });
    const byKey = new Map<string, KybEntityRegistrationDto>();
    for (const dto of response.data) {
      byKey.set(registrationKey(dto.idType, dto.idValue), dto);
    }
    return byKey;
  }

  private async addRegistration(
    caseId: string,
    idType: string,
    idValue: string,
    countryCode: string,
    issuedDate?: string,
  ): Promise<KybEntityRegistrationDto> {
    const response = await this.httpClient.post<
      ApiResponse<KybEntityRegistrationDto>,
      { idType: string; idValue: string; countryCode: string; issuedDate?: string }
    >({
      url: kybEndpoints.entityRegistrations(caseId),
      body: { idType, idValue, countryCode, issuedDate },
      withAuth: true,
    });
    return response.data;
  }

  /**
   * Ajoute au dossier réel toute immatriculation pas encore synchronisée avec le back (#24424) ;
   * une ligne déjà présente (même idType + idValue) est réutilisée telle quelle. Le statut de
   * vérification n'est jamais écrit ici : il vient uniquement du back (NON_VERIFIE à la création,
   * décidé ensuite par la conformité sur une ressource dédiée, avec preuve).
   */
  private async syncImmatriculations(
    caseId: string,
    rows: Immatriculation[],
    existing: Map<string, KybEntityRegistrationDto>,
  ): Promise<Immatriculation[]> {
    const result: Immatriculation[] = [];
    for (const row of rows) {
      const { registryType: idType, number: idValue, country } = row;
      if (!idType || !idValue || !country) {
        result.push({ ...row, status: "NON_VERIFIE" });
        continue;
      }
      const key = registrationKey(idType, idValue);
      let dto = existing.get(key);
      if (!dto) {
        dto = await this.addRegistration(caseId, idType, idValue, country, row.date);
        existing.set(key, dto);
      }
      result.push({
        ...row,
        registrationId: dto.registrationId,
        date: dto.issuedDate ?? row.date,
        status: dto.verificationStatus,
      });
    }
    return result;
  }

  /** Même logique que {@link syncImmatriculations}, pour les identifiants fiscaux (NIF/IFU/NINEA/AUTRE). */
  private async syncTaxIdentifiers(
    caseId: string,
    rows: TaxIdentifier[],
    existing: Map<string, KybEntityRegistrationDto>,
  ): Promise<TaxIdentifier[]> {
    const result: TaxIdentifier[] = [];
    for (const row of rows) {
      const { type: idType, value: idValue, country } = row;
      if (!idType || !idValue || !country) {
        result.push({ ...row, status: "NON_VERIFIE" });
        continue;
      }
      const key = registrationKey(idType, idValue);
      let dto = existing.get(key);
      if (!dto) {
        dto = await this.addRegistration(caseId, idType, idValue, country);
        existing.set(key, dto);
      }
      result.push({
        ...row,
        registrationId: dto.registrationId,
        status: dto.verificationStatus,
      });
    }
    return result;
  }

  /**
   * Le dossier KYB (case) est le porteur de la checklist documentaire, des représentants et de
   * l'actionnariat côté back — mais le front (comme le reste de l'app) ne connaît que le
   * businessKey. L'ouverture est idempotente (#24423) : un rejeu renvoie le dossier existant, donc
   * appeler ceci à chaque action au lieu de mettre le caseId en cache ne crée jamais de doublon et
   * évite un état de session supplémentaire à invalider.
   */
  private async resolveCaseId(businessKey: string): Promise<string> {
    const response = await this.httpClient.post<
      ApiResponse<KybLegalEntityCaseDto>,
      { businessKey: string }
    >({
      url: kybEndpoints.openOrGetCase(),
      body: { businessKey },
      withAuth: true,
    });
    return response.data.caseId;
  }

  /**
   * Bloc `legalEntity` du PATCH prospect (#26005, point 1) : capital, date de constitution, adresse
   * du siège, objet social, secteur d'activité, nom commercial, ville — tous les champs de l'écran
   * Entreprise qui n'ont pas déjà leur propre canal de persistance (immatriculations/taxIdentifiers
   * via le dossier KYB ; raison sociale/téléphone/email/pays/forme juridique verrouillés, saisis à
   * l'écran 01). N'envoie que les champs renseignés (mise à jour partielle, comme le contrat back).
   */
  private buildLegalEntityPatch(
    data: EntrepriseIdentification,
  ): LegalEntityDetailsPatchDto | null {
    const patch: LegalEntityDetailsPatchDto = {};
    if (data.shareCapital !== undefined && data.shareCapital !== null) {
      patch.shareCapital = data.shareCapital;
    }
    if (data.incorporationDate) {
      patch.incorporationDate = data.incorporationDate;
    }
    if (data.headOfficeAddress) {
      patch.headOfficeAddress = data.headOfficeAddress;
    }
    if (data.corporateObject) {
      patch.corporatePurpose = data.corporateObject;
    }
    if (data.activitySector) {
      patch.sectorActivityCode = data.activitySector;
    }
    if (data.tradeName) {
      patch.tradeName = data.tradeName;
    }
    if (data.city) {
      patch.city = data.city;
    }
    return Object.keys(patch).length > 0 ? patch : null;
  }

  private async syncCompanyDetails(
    businessKey: string,
    data: EntrepriseIdentification,
  ): Promise<void> {
    const legalEntity = this.buildLegalEntityPatch(data);
    if (!legalEntity) {
      return;
    }
    await this.httpClient.patch<ApiResponse<unknown>, { legalEntity: LegalEntityDetailsPatchDto }>({
      url: updateCustomerKYCUrl(businessKey),
      body: { legalEntity },
      withAuth: true,
    });
  }

  /**
   * Immatriculations et identifiants fiscaux (#24424) sont persistés via le dossier KYB. Les
   * détails d'entreprise (capital, date, adresse, objet, secteur, nom commercial, ville) sont
   * persistés via `PATCH /customers/prospect/{businessKey}` (#26005, point 1, livré le même jour
   * que ce câblage).
   */
  async saveEntrepriseIdentification(
    businessKey: string,
    data: EntrepriseIdentification,
  ): Promise<RepositoryResult<EntrepriseIdentification>> {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.success(data);
    }

    try {
      const caseId = await this.resolveCaseId(businessKey);
      const existing = await this.fetchExistingRegistrations(caseId);
      const immatriculations = await this.syncImmatriculations(
        caseId,
        data.immatriculations,
        existing,
      );
      const taxIdentifiers = await this.syncTaxIdentifiers(
        caseId,
        data.taxIdentifiers,
        existing,
      );
      await this.syncCompanyDetails(businessKey, data);
      return RepositoryResultFactory.success({
        ...data,
        immatriculations,
        taxIdentifiers,
      });
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  /**
   * Relit les immatriculations/identifiants fiscaux déjà persistés (AC-3) : appelée à
   * l'ouverture du dossier, avant tout PATCH/POST, pour que la conformité voie et puisse
   * décider ce qu'un agent — ou elle-même, une session précédente — a déjà déposé.
   */
  async getExistingRegistrations(
    businessKey: string,
  ): Promise<
    RepositoryResult<{
      immatriculations: Immatriculation[];
      taxIdentifiers: TaxIdentifier[];
      /**
       * AC-9 : distingue « jamais rien saisi » de « saisi puis tout retiré ». Les deux donnent une
       * liste vide ci-dessus, mais seul le premier cas doit laisser le préremplissage RCCM du
       * prospect s'afficher côté écran.
       */
      hasEverHadRegistrations: boolean;
    }>
  > {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.success({
        immatriculations: [],
        taxIdentifiers: [],
        hasEverHadRegistrations: false,
      });
    }

    try {
      const caseId = await this.resolveCaseId(businessKey);
      const existing = await this.fetchExistingRegistrations(caseId);
      const historyResponse = await this.httpClient.get<ApiResponse<boolean>>({
        url: kybEndpoints.entityRegistrationHistory(caseId),
        withAuth: true,
      });
      const immatriculations: Immatriculation[] = [];
      const taxIdentifiers: TaxIdentifier[] = [];
      for (const dto of existing.values()) {
        if (TAX_IDENTIFIER_ID_TYPES.has(dto.idType)) {
          taxIdentifiers.push({
            id: crypto.randomUUID(),
            registrationId: dto.registrationId,
            type: dto.idType,
            value: dto.idValue,
            country: dto.countryCode,
            status: dto.verificationStatus,
          });
        } else {
          immatriculations.push({
            id: crypto.randomUUID(),
            registrationId: dto.registrationId,
            registryType: dto.idType,
            number: dto.idValue,
            country: dto.countryCode,
            date: dto.issuedDate ?? undefined,
            status: dto.verificationStatus,
          });
        }
      }
      return RepositoryResultFactory.success({
        immatriculations,
        taxIdentifiers,
        hasEverHadRegistrations: historyResponse.data,
      });
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  /** AC-9 : retire une immatriculation ou un identifiant fiscal déjà persisté. */
  async removeRegistration(
    businessKey: string,
    registrationId: string,
  ): Promise<RepositoryResult<void>> {
    return executeVoidRequest(async () => {
      const caseId = await this.resolveCaseId(businessKey);
      return this.httpClient.delete<ApiResponse<void>>({
        url: kybEndpoints.entityRegistration(caseId, registrationId),
        withAuth: true,
      });
    });
  }

  async getRelationRoles(
    ecran?: KybRelationScreen,
  ): Promise<RepositoryResult<KybRelationRole[]>> {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.success(
        generateMockRelationRoles(ecran),
      );
    }

    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<KybRelationRoleDto[]>>({
          url: kybEndpoints.relationRoles(ecran),
          withAuth: true,
        }),
      (dtos) => dtos.map(mapRelationRole),
    );
  }

  async listRepresentatives(
    businessKey: string,
  ): Promise<RepositoryResult<KybRepresentativePerson[]>> {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.success([]);
    }

    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybRepresentativeDto[]>>({
          url: kybEndpoints.representatives(caseId),
          withAuth: true,
        });
      },
      (dtos) => dtos.map(mapRepresentative),
    );
  }

  async searchRepresentativeCandidates(
    businessKey: string,
    lastName: string,
    firstName: string,
    birthDate?: string,
  ): Promise<RepositoryResult<KybPersonCandidate[]>> {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.success(
        generateMockRepresentativeCandidates(),
      );
    }

    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybPersonCandidateDto[]>>({
          url: kybEndpoints.representativeCandidates(
            caseId,
            lastName,
            firstName,
            birthDate,
          ),
          withAuth: true,
        });
      },
      (dtos) => dtos.map(mapPersonCandidate),
    );
  }

  /**
   * AC-2 : une réponse 409 porteuse de candidats n'est pas une erreur système — elle est
   * remontée comme un résultat `success` de forme `{ outcome: "duplicate" }`, à charge de
   * l'écran d'en faire quelque chose. `executeRequest` ne s'y prête pas (il traite tout rejet
   * comme un échec) : cette méthode gère donc elle-même son essai/rattrapage.
   */
  async addRepresentative(
    businessKey: string,
    command: AddRepresentativeCommand,
  ): Promise<RepositoryResult<AddRepresentativeOutcome>> {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.success({
        outcome: "added",
        representative: {
          relatedPersonId: crypto.randomUUID(),
          roleCode: command.roleCode,
          roleCategory: "DIRIGEANT",
          lastName: command.lastName,
          firstName: command.firstName,
          birthDate: command.birthDate,
          nationalityCode: command.nationalityCode,
          professionCode: command.professionCode,
          personCustomerId: command.personCustomerId,
          source: command.personCustomerId ? "RIC_PROJECTION" : "AGENT_INPUT",
          createdAt: new Date().toISOString(),
        },
      });
    }

    try {
      const caseId = await this.resolveCaseId(businessKey);
      const response = await this.httpClient.post<
        ApiResponse<KybRepresentativeDto>,
        AddRepresentativeCommand
      >({
        url: kybEndpoints.representatives(caseId),
        body: command,
        withAuth: true,
      });
      return RepositoryResultFactory.success({
        outcome: "added",
        representative: mapRepresentative(response.data),
      });
    } catch (error) {
      const candidates = extractDuplicateCandidates(error);
      if (candidates) {
        return RepositoryResultFactory.success({
          outcome: "duplicate",
          candidates: candidates.map(mapPersonCandidate),
        });
      }
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  async addRepresentativeRole(
    businessKey: string,
    relatedPersonId: string,
    roleCode: string,
  ): Promise<RepositoryResult<KybPersonRole>> {
    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<
          ApiResponse<KybPersonRoleDto>,
          { roleCode: string }
        >({
          url: kybEndpoints.representativeRoles(caseId, relatedPersonId),
          body: { roleCode },
          withAuth: true,
        });
      },
      mapPersonRole,
    );
  }

  async listRepresentativeRoles(
    businessKey: string,
    relatedPersonId: string,
  ): Promise<RepositoryResult<KybPersonRole[]>> {
    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybPersonRoleDto[]>>({
          url: kybEndpoints.representativeRoles(caseId, relatedPersonId),
          withAuth: true,
        });
      },
      (dtos) => dtos.map(mapPersonRole),
    );
  }

  async removeRepresentativeRole(
    businessKey: string,
    relatedPersonId: string,
    personRoleId: string,
  ): Promise<RepositoryResult<void>> {
    return executeVoidRequest(async () => {
      const caseId = await this.resolveCaseId(businessKey);
      return this.httpClient.delete<ApiResponse<void>>({
        url: kybEndpoints.representativeRole(
          caseId,
          relatedPersonId,
          personRoleId,
        ),
        withAuth: true,
      });
    });
  }

  async addLegalParty(
    businessKey: string,
    command: AddLegalPartyCommand,
  ): Promise<RepositoryResult<KybLegalParty>> {
    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<
          ApiResponse<KybLegalPartyDto>,
          AddLegalPartyCommand
        >({
          url: kybEndpoints.ownershipLegalParties(caseId),
          body: command,
          withAuth: true,
        });
      },
      mapLegalParty,
    );
  }

  async listLegalParties(
    businessKey: string,
  ): Promise<RepositoryResult<KybLegalParty[]>> {
    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybLegalPartyDto[]>>({
          url: kybEndpoints.ownershipLegalParties(caseId),
          withAuth: true,
        });
      },
      (dtos) => dtos.map(mapLegalParty),
    );
  }

  async addOwnershipEdge(
    businessKey: string,
    command: AddOwnershipEdgeCommand,
  ): Promise<RepositoryResult<KybOwnershipEdge>> {
    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<
          ApiResponse<KybOwnershipEdgeDto>,
          AddOwnershipEdgeCommand
        >({
          url: kybEndpoints.ownershipEdges(caseId),
          body: command,
          withAuth: true,
        });
      },
      mapOwnershipEdge,
    );
  }

  async listOwnershipEdges(
    businessKey: string,
  ): Promise<RepositoryResult<KybOwnershipEdge[]>> {
    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybOwnershipEdgeDto[]>>({
          url: kybEndpoints.ownershipEdges(caseId),
          withAuth: true,
        });
      },
      (dtos) => dtos.map(mapOwnershipEdge),
    );
  }

  async removeOwnershipEdge(
    businessKey: string,
    ownershipEdgeId: string,
  ): Promise<RepositoryResult<void>> {
    return executeVoidRequest(async () => {
      const caseId = await this.resolveCaseId(businessKey);
      return this.httpClient.delete<ApiResponse<void>>({
        url: kybEndpoints.ownershipEdge(caseId, ownershipEdgeId),
        withAuth: true,
      });
    });
  }

  async computeBeneficialOwners(
    businessKey: string,
  ): Promise<RepositoryResult<KybBeneficialOwner[]>> {
    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybBeneficialOwnerDto[]>>({
          url: kybEndpoints.ownershipBeneficialOwners(caseId),
          withAuth: true,
        });
      },
      (dtos) => dtos.map(mapBeneficialOwner),
    );
  }

  async assessOpacity(
    businessKey: string,
  ): Promise<RepositoryResult<KybOpacityAssessment>> {
    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybOpacityAssessmentDto>>({
          url: kybEndpoints.ownershipOpacity(caseId),
          withAuth: true,
        });
      },
      mapOpacityAssessment,
    );
  }

  async saveShareholding(
    _businessKey: string,
    data: Shareholding,
  ): Promise<RepositoryResult<Shareholding>> {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.success(data);
    }

    // NOSONAR S1135 — no real KYB shareholding persistence endpoint exists yet.
    // Once one ships: replace this branch with an executeRequest call against
    // the real endpoint and flip VITE_USE_MOCK_KYB_ENTITY off. Nothing else
    // changes, since KybRepository's interface doesn't change.
    return RepositoryResultFactory.errorWithoutException(
      "KYB shareholding persistence endpoint not implemented yet.",
    );
  }

  async checkActiveSignatoryStatus(
    existingCustomerId: string,
  ): Promise<RepositoryResult<{ isActiveSignatory: boolean }>> {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.success({
        isActiveSignatory: isMockActiveSignatory(existingCustomerId),
      });
    }

    // NOSONAR S1135 — no real active-signatory-status endpoint exists yet.
    // Once it ships: replace this branch with an executeRequest call against
    // the real endpoint and flip VITE_USE_MOCK_KYB_ENTITY off. Nothing else
    // changes, since KybRepository's interface doesn't change.
    return RepositoryResultFactory.errorWithoutException(
      "Active signatory status endpoint not implemented yet.",
    );
  }

  async generateDocumentChecklist(
    businessKey: string,
    legalForm: string,
    incorporationCountry: string,
  ): Promise<RepositoryResult<DocumentChecklist>> {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.success(
        generateMockDocumentChecklist(businessKey, legalForm, incorporationCountry),
      );
    }

    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybCaseDocumentChecklistDto>>({
          url: kybEndpoints.documentChecklist(caseId),
          withAuth: true,
        });
      },
      (dto) => mapChecklist(businessKey, dto),
    );
  }

  async refreshDocumentChecklist(
    businessKey: string,
  ): Promise<RepositoryResult<DocumentChecklist>> {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.errorWithoutException(
        "refreshDocumentChecklist n'a pas de repli mock : appeler generateDocumentChecklist.",
      );
    }

    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybCaseDocumentChecklistDto>>({
          url: kybEndpoints.documentChecklist(caseId),
          withAuth: true,
        });
      },
      (dto) => mapChecklist(businessKey, dto),
    );
  }

  /**
   * Dépose une pièce au niveau du dossier (écran 05). Un second dépôt du même `typePieceCode`
   * REMPLACE le précédent (sauf pièce déjà `VALIDE`, 409 dans ce cas) — géré côté back, rien à
   * gérer ici.
   */
  async uploadCaseDocument(
    businessKey: string,
    typePieceCode: string,
    file: File,
  ): Promise<RepositoryResult<CaseDocumentUploadResult>> {
    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        const formData = new FormData();
        formData.append("typePieceCode", typePieceCode);
        formData.append("file", file);
        return this.httpClient.post<ApiResponse<KybCaseDocumentUploadDto>, FormData>({
          url: kybEndpoints.documentUpload(caseId),
          body: formData,
          withAuth: true,
        });
      },
      (dto) => ({
        personDocumentId: dto.personDocumentId,
        typePieceCode: dto.typePieceCode,
        fileName: dto.fileName,
      }),
    );
  }

  /** Rattache la pièce déposée (`uploadCaseDocument`) à l'exigence, et fixe le statut de la checklist. */
  async updateCaseDocumentStatus(
    businessKey: string,
    caseDocumentId: string,
    statut: CaseDocumentStatus,
    documentId?: string,
  ): Promise<RepositoryResult<DocumentChecklistItem>> {
    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.put<
          ApiResponse<KybCaseDocumentDto>,
          { statut: CaseDocumentStatus; documentId?: string }
        >({
          url: kybEndpoints.documentChecklistItem(caseId, caseDocumentId),
          body: { statut, documentId },
          withAuth: true,
        });
      },
      mapChecklistItem,
    );
  }

  /** AC-3/AC-P1 : le fichier est envoyé tel quel, le SHA-256 est calculé côté serveur. */
  async uploadRegistrationProof(
    businessKey: string,
    registrationId: string,
    file: File,
  ): Promise<RepositoryResult<RegistrationProofUploadResult>> {
    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        const formData = new FormData();
        formData.append("file", file);
        return this.httpClient.post<
          ApiResponse<KybEntityRegistrationProofDto>,
          FormData
        >({
          url: kybEndpoints.entityRegistrationProof(caseId, registrationId),
          body: formData,
          withAuth: true,
        });
      },
      (dto) => ({
        proofDocumentId: dto.proofDocumentId,
        sha256: dto.sha256,
        fileName: dto.fileName,
      }),
    );
  }

  /** AC-2/AC-3/AC-5 : la décision porte le `proofDocumentId`/`proofSha256` reçus de l'upload, jamais recalculés. */
  async decideRegistrationVerification(
    businessKey: string,
    registrationId: string,
    decision: RegistrationVerificationDecisionInput,
  ): Promise<RepositoryResult<RegistrationVerificationDecisionResult>> {
    return executeRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<
          ApiResponse<KybEntityRegistrationVerifyResponseDto>,
          RegistrationVerificationDecisionInput
        >({
          url: kybEndpoints.entityRegistrationVerification(
            caseId,
            registrationId,
          ),
          body: decision,
          withAuth: true,
        });
      },
      (dto) => ({
        status: dto.verificationStatus,
        verificationSource: dto.verificationSource ?? undefined,
        verifiedBy: dto.verifiedBy ?? undefined,
        verifiedAt: dto.verifiedAt ?? undefined,
      }),
    );
  }

  async runScreening(
    _businessKey: string,
    subjects: ScreeningSubject[],
  ): Promise<RepositoryResult<ScreeningResult[]>> {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.success(
        generateMockScreeningResults(subjects),
      );
    }

    // NOSONAR S1135 — no real screening endpoint exists yet
    // (US-KYB-07-01 / #25467). Screening is executed and qualified by
    // compliance outside this onboarding flow (RIC/VNeuron) — this method
    // only projects the result, it never launches or re-runs a check. Once
    // a projection endpoint ships: replace this branch with an
    // executeRequest call against it and flip VITE_USE_MOCK_KYB_ENTITY off.
    // Nothing else changes, since KybRepository's interface doesn't change.
    return RepositoryResultFactory.errorWithoutException(
      "Screening projection endpoint not implemented yet.",
    );
  }

  async computeRiskScore(
    businessKey: string,
    inputs: RiskScoreInputs,
  ): Promise<RepositoryResult<RiskScore>> {
    if (isKybEntityMockEnabled()) {
      const { compositeScore, level, factors } = generateMockRiskScore(inputs);
      return RepositoryResultFactory.success({
        businessKey,
        compositeScore,
        level,
        factors,
      });
    }

    // NOSONAR S1135 — no real composite-scoring endpoint exists yet, and no
    // dedicated backend user story was identified under EPIC-KYB-07 for this
    // (#25468; clarify with the backend owner). Once one ships: replace this
    // branch with an executeRequest call against the real endpoint and flip
    // VITE_USE_MOCK_KYB_ENTITY off. Nothing else changes, since
    // KybRepository's interface doesn't change.
    return RepositoryResultFactory.errorWithoutException(
      "Risk score computation endpoint not implemented yet.",
    );
  }

  async resolveDecision(
    businessKey: string,
    inputs: DecisionResolutionInputs,
    resolvedAt: string,
  ): Promise<RepositoryResult<KybDecision>> {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.success(
        generateMock4EyesDecision(businessKey, inputs, resolvedAt),
      );
    }

    // NOSONAR S1135 — no real 4-eyes decision endpoint exists yet
    // (US-KYB-04-03/04-04 / #25465). Once it ships: replace this branch with
    // an executeRequest call against the real endpoint and flip
    // VITE_USE_MOCK_KYB_ENTITY off. Nothing else changes, since
    // KybRepository's interface doesn't change.
    return RepositoryResultFactory.errorWithoutException(
      "4-eyes decision resolution endpoint not implemented yet.",
    );
  }

  async createAccount(
    businessKey: string,
    signatoryIds: string[],
  ): Promise<RepositoryResult<AccountOpeningResult>> {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.success({
        businessKey,
        status: "CREATED",
        accountNumber: generateMockAccountNumber(businessKey),
        signatoryIds,
      });
    }

    // NOSONAR S1135 — no real Orion account-opening endpoint exists yet
    // (#25466). This action is irreversible once a real endpoint exists —
    // do not add a mock "success by default" fallback here beyond the
    // explicit VITE_USE_MOCK_KYB_ENTITY switch. Once it ships: replace this
    // branch with an executeRequest call against the real endpoint and flip
    // VITE_USE_MOCK_KYB_ENTITY off. Nothing else changes, since
    // KybRepository's interface doesn't change.
    return RepositoryResultFactory.errorWithoutException(
      "Account opening endpoint not implemented yet.",
    );
  }

  async generateEvidencePack(
    businessKey: string,
    generatedAt: string,
    inputs: EvidencePackGenerationInputs,
  ): Promise<RepositoryResult<EvidencePack>> {
    if (isKybEntityMockEnabled()) {
      return RepositoryResultFactory.success(
        generateMockEvidencePack(businessKey, generatedAt, inputs),
      );
    }

    // NOSONAR S1135 — no real evidence-pack generation endpoint exists yet
    // (US-KYB-08-01 / #25469). Once it ships: replace this branch with an
    // executeRequest call against the real endpoint and flip
    // VITE_USE_MOCK_KYB_ENTITY off. Nothing else changes, since
    // KybRepository's interface doesn't change.
    return RepositoryResultFactory.errorWithoutException(
      "Evidence pack generation endpoint not implemented yet.",
    );
  }
}
